import { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, BookOpen, ChevronRight } from "lucide-react";
import { guideEntries, guideCategories, type GuideEntry } from "../guide";
import { GuideContent } from "./GuideContent";

interface GuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GuideModal({ isOpen, onClose }: GuideModalProps) {
  const [search, setSearch] = useState("");
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearch("");
      setSelectedSlug(null);
    }
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const filtered = useMemo(() => {
    if (!search.trim()) return guideEntries;
    const q = search.toLowerCase();
    return guideEntries.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.content.toLowerCase().includes(q)
    );
  }, [search]);

  const selectedEntry = useMemo(
    () => guideEntries.find((e) => e.slug === selectedSlug) ?? null,
    [selectedSlug]
  );

  const handleBack = useCallback(() => setSelectedSlug(null), []);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[99] bg-overlay/50"
            onClick={onClose}
          />

          {/* Floating modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", damping: 28, stiffness: 400 }}
            className="fixed inset-0 z-[100] m-auto flex h-[min(75vh,640px)] w-[min(95vw,520px)] flex-col rounded-2xl bg-surface shadow-softLg border border-border/30"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                {selectedEntry && (
                  <button
                    onClick={handleBack}
                    className="mr-1 rounded-lg p-1.5 text-muted transition-colors hover:bg-foreground/5 hover:text-foreground"
                    aria-label="Back to guide list"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                  </button>
                )}
                <BookOpen className="h-5 w-5 text-accent" />
                <h2 className="font-display text-lg font-semibold tracking-tight text-foreground">
                  {selectedEntry ? selectedEntry.title : "Guide"}
                </h2>
              </div>
              <button
                onClick={onClose}
                className="rounded-lg p-1.5 text-muted transition-colors hover:bg-foreground/5 hover:text-foreground"
                aria-label="Close guide"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search (only on index view) */}
            {!selectedEntry && (
              <div className="px-5 pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search guides..."
                    className="w-full rounded-xl border border-border/40 bg-foreground/5 py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted/60 outline-none transition-shadow focus:ring-2 focus:ring-accent/20"
                  />
                </div>
              </div>
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 pb-6">
              {selectedEntry ? (
                <GuideContent markdown={selectedEntry.content} />
              ) : (
                <GuideIndex
                  entries={filtered}
                  onSelect={setSelectedSlug}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function GuideIndex({
  entries,
  onSelect,
}: {
  entries: GuideEntry[];
  onSelect: (slug: string) => void;
}) {
  return (
    <div className="space-y-5">
      {guideCategories.map((cat) => {
        const items = entries.filter((e) => e.category === cat.key);
        if (items.length === 0) return null;
        return (
          <div key={cat.key}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted/70">
              {cat.label}
            </h3>
            <div className="space-y-1">
              {items.map((entry) => (
                <button
                  key={entry.slug}
                  onClick={() => onSelect(entry.slug)}
                  className="flex w-full items-center justify-between rounded-xl px-4 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-foreground/5"
                >
                  {entry.title}
                  <ChevronRight className="h-4 w-4 text-muted" />
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {entries.length === 0 && (
        <p className="py-8 text-center text-sm text-muted">
          No guides match your search.
        </p>
      )}
    </div>
  );
}
